import { useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaEdit, FaPrint } from "react-icons/fa";
import { format } from "date-fns";
import { toast } from "react-toastify";
import axios from "axios";
import GenerateBillModal from "./GenerateBillModal";
import { PrintLayout } from "../PrintLayout";

const BILL_API = "https://api.muktihospital.com/api/medicine-bill";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const ViewBillModal = ({ isOpen, onClose, bill, onEdit, onDelete }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  if (!isOpen || !bill) return null;
  // Destructure bill with fallback for items array
  const {
    id,
    billNumber,
    createdAt,
    customerName,
    paymentMode,
    paymentAmount = 0,
    discount = 0,
    taxAmount = 0,
    netAmount = 0,
    dueAmount = 0,
    returnAmount = 0,
    note,
    documentUrl,
    items = [],
  } = bill;

  // Normalize items data
  const medicineList =
    items.length > 0
      ? items.map((med) => ({
          ...med,
          medicineName: med.medicineName || "N/A",
          medicineCategory: med.medicineCategory || "N/A",
          batchNo: med.batchNo || "N/A",
          quantity: med.quantity || 0,
          salePrice: med.salePrice || 0,
        }))
      : [];

  // Calculate sub-total
  const subTotal = Math.round(
  medicineList.reduce(
    (sum, med) => sum + med.quantity * med.salePrice * (1 + med.tax / 100),
    0
  )
); 

  const handlePrintBill = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
     setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ''));
      if (printLayout) {
        printLayout.style.display = 'none';
      }
    }, 500);
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BILL_API}/${id}`, {
        headers: { "X-Api-Key": API_KEY },
      });
      onDelete(id);
      onClose();
      toast.success("Bill deleted successfully!", { autoClose: 3000 });
    } catch (err) {
      console.error("Error deleting bill:", err);
      toast.error(`Failed to delete bill: ${err.message || "Network error"}`, {
        autoClose: 3000,
      });
    }
  };

  // Define PrintLayout props
  const billInfoContent = (
    <>
      <div>
        <p>
          <strong>Invoice No:</strong> {billNumber}
        </p>
        <p>
          <strong>Billing Date:</strong>{" "}
          {createdAt ? format(new Date(createdAt), "MM/dd/yyyy") : "N/A"}
        </p>
        <p>
          <strong>Customer Name:</strong> {customerName || "N/A"}
        </p>
      </div>
      <div>
        <p>
          <strong>Payment Mode:</strong>{" "}
          {paymentMode ? paymentMode.charAt(0).toUpperCase() + paymentMode.slice(1) : "N/A"}
        </p>
        <p>
          <strong>Document:</strong>{" "}
          {documentUrl ? (
            <a
              href={`https://api.muktihospital.com${documentUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </a>
          ) : (
            "None"
          )}
        </p>
      </div>
    </>
  );

  const tableContent = (
    <>
      <thead>
        <tr>
          <th>Sl No</th>
          <th>Medicine Name</th>
          <th>Category</th>
          <th>Batch No</th>
          <th>Quantity</th>
          <th>Unit Price (৳)</th>
        </tr>
      </thead>
      <tbody>
        {medicineList.length > 0 ? (
          medicineList.map((med, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{med.medicineName}</td>
              <td>{med.medicineCategory}</td>
              <td>{med.batchNo}</td>
              <td>{med.quantity}</td>
              <td>৳ {med.salePrice.toFixed(2)}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="6" style={{ textAlign: "center" }}>
              No items available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  const summaryContent = (
    <>
      <div>
        <span>Sub Total</span>
        <span>৳ {subTotal}</span>
      </div>
      <div>
        <span>Vat</span>
        <span>৳ {taxAmount.toFixed(2)}</span>
      </div>
      <div>
        <span>Discount</span>
        <span>৳ {discount.toFixed(2)}</span>
      </div>
      <div>
        <span>Net Total</span>
        <span>৳ {netAmount.toFixed(2)}</span>
      </div>
      <div>
        <span>Paid Amount</span>
        <span>৳ {paymentAmount.toFixed(2)}</span>
      </div>
      <div>
        <span>Due Amount</span>
        <span>৳ {dueAmount.toFixed(2)}</span>
      </div>
      <div>
        <span>Return Amount</span>
        <span>৳ {returnAmount.toFixed(2)}</span>
      </div>
    </>
  );

  const noteContent = note && (
    <p className="note-box">
      <strong>Note:</strong> {note}
    </p>
  );

  return (
    <>
      {/* Modal Content */}
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Bill Details
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrintBill}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={handleEdit}
                className="size-7 rounded-full bg-white text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={handleDelete}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiXMark />
              </button>
            </div>
          </div>

          <div className="px-4 print-hidden">
            {/* Invoice Info */}
            <div className="mt-4 sm:flex justify-between items-start mb-2">
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Invoice No </span>:
                  <span className="text-gray-600 pl-1">{billNumber}</span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Customer Name </span>:
                  <span className="text-gray-600 pl-1">{customerName || "N/A"}</span>
                </p>
              </div>
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Billing Date</span>:
                  <span className="text-gray-600 pl-1">
                    {createdAt
                      ? format(new Date(createdAt), "MM/dd/yyyy")
                      : "N/A"}
                  </span>
                </p>
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Payment Mode</span>:
                  <span className="text-gray-600 capitalize pl-1">
                    {paymentMode || "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Document</span>:
                  <span className="text-gray-600 pl-1">
                    {documentUrl ? (
                      <a
                        href={`https://api.muktihospital.com${documentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View
                      </a>
                    ) : (
                      "None"
                    )}
                  </span>
                </p>
              </div>
            </div>

            {/* Medicine Table */}
            {medicineList.length > 0 && (
              <div className="mt-4 sm:mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 sm:p-3 border font-semibold">
                          Medicine Name
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold">
                          Category
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold">
                          Batch No
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold">
                          Quantity
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold text-right">
                          Unit Price (৳)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicineList.map((med, index) => (
                        <tr key={index}>
                          <td className="p-2 sm:p-3 border text-center">
                            {med.medicineName}
                          </td>
                          <td className="p-2 sm:p-3 border text-center">
                            {med.medicineCategory}
                          </td>
                          <td className="p-2 sm:p-3 border text-center">
                            {med.batchNo}
                          </td>
                          <td className="p-2 sm:p-3 border text-center">
                            {med.quantity}
                          </td>
                          <td className="p-2 sm:p-3 border text-right">
                            {med.salePrice.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {/* Calculation Area */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4"
                          colSpan="4"
                        >
                          <p>
                            <strong>Sub Total (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{subTotal.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="4"
                        >
                          <p>
                            <strong>Vat (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{taxAmount.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="4"
                        >
                          <p>
                            <strong>Discount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{discount.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800"
                          colSpan="4"
                        >
                          <strong className="py-1 px-3 text-sm">
                            Net Total (৳) :
                          </strong>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                          <strong className="py-1 px-3 border-t border-gray-400/20 block">
                            {netAmount.toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="4"
                        >
                          <p>
                            <strong>Return Amount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{returnAmount.toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="4"
                        >
                          <p>
                            <strong>Due Amount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{dueAmount.toFixed(2)}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payment Note */}
            {note && (
              <p className="text-[15px] text-black mb-1 max-w-[600px] mt-4">
                <span className="inline-block mr-1 capitalize font-inter text-M-heading-color font-semibold">
                  Note:{" "}
                </span>
                <span className="text-M-text-color">{note}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Print Layout */}
      <PrintLayout
        className="print-layout"
        billInfo={billInfoContent}
        table={tableContent}
        summary={summaryContent}
        note={noteContent}
        title="Pharmacy Bill"
      />

      {/* Edit Modal */}
      {isEditModalOpen && (
        <GenerateBillModal
          isOpen={isEditModalOpen}
          billId={id}
          onClose={() => {
            setIsEditModalOpen(false);
            onClose();
          }}
          bill={bill}
          onSave={async (message, updatedBill) => {
            await onEdit(message, updatedBill);
            setIsEditModalOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default ViewBillModal;